"use client";

import { ChevronDown } from "lucide-react";
import { useEffect, useId, useMemo, useState } from "react";
import { TranscriptionMessage } from "./TranscriptionMessage";

interface ContinuationContextProps {
  messages: string[];
  assistantLabel?: string;
}

const DISPLAY_MESSAGE_LIMIT = 3;

function toPreviewText(message: string): string {
  return message
    .replace(/!\[([^\]]*)\]\([^)]*\)/g, "$1")
    .replace(/\[([^\]]+)\]\([^)]*\)/g, "$1")
    .replace(/```[\s\S]*?```/g, " ")
    .replace(/^\s{0,3}(?:#{1,6}|>|[-+*])\s+/gm, "")
    .replace(/[`*_~]+/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

export function ContinuationContext({
  messages,
  assistantLabel = "Tutor",
}: ContinuationContextProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const regionId = useId();
  const displayMessages = useMemo(
    () =>
      messages
        .map((message) => message.trim())
        .filter(Boolean)
        .slice(-DISPLAY_MESSAGE_LIMIT),
    [messages]
  );
  const contextKey = displayMessages.join("\u0000");
  const latestMessage = displayMessages.at(-1) || "";

  useEffect(() => {
    setIsExpanded(false);
  }, [contextKey]);

  if (displayMessages.length === 0) return null;

  const messageCountLabel = `${displayMessages.length} tutor ${
    displayMessages.length === 1 ? "message" : "messages"
  }`;

  return (
    <section className="mb-4" aria-label="Previous tutor context">
      <div className="overflow-hidden rounded-lg border border-white/10 bg-white/[0.035]">
        <button
          type="button"
          className="flex min-h-11 w-full items-center gap-3 px-3 py-2 text-left text-sm transition-colors hover:bg-white/[0.035] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-orange-300/70 active:bg-white/[0.055]"
          aria-expanded={isExpanded}
          aria-controls={regionId}
          onClick={() => setIsExpanded((current) => !current)}
        >
          <span className="min-w-0 flex-1">
            <span className="font-semibold text-white/80">Previous session</span>
            <span className="ml-2 text-xs text-white/40">{messageCountLabel}</span>
          </span>
          <span className="text-xs font-semibold text-orange-200/80">
            {isExpanded ? "Hide" : "Show"}
          </span>
          <ChevronDown
            aria-hidden="true"
            size={16}
            strokeWidth={2}
            className={`shrink-0 text-white/45 transition-transform duration-150 motion-reduce:transition-none ${
              isExpanded ? "rotate-180" : ""
            }`}
          />
        </button>

        <div id={regionId}>
          {isExpanded ? (
            <div className="divide-y divide-white/10 border-t border-white/10">
              {displayMessages.map((message, index) => (
                <div key={`continued-tutor-${index}`} className="px-3 py-3 text-white/65 sm:px-4">
                  <TranscriptionMessage
                    id={`continued-tutor-${index}`}
                    role="assistant"
                    content={message}
                    isFinal
                    assistantLabel={assistantLabel}
                  />
                </div>
              ))}
            </div>
          ) : (
            <div className="border-t border-white/10 px-3 pb-3 pt-2.5 sm:px-4">
              <div className="mb-1 text-[11px] font-semibold uppercase text-orange-300/70">
                {assistantLabel}
              </div>
              <p
                className="line-clamp-2 text-sm leading-6 text-white/60"
                title={toPreviewText(latestMessage)}
              >
                {toPreviewText(latestMessage)}
              </p>
            </div>
          )}
        </div>
      </div>

      <div className="mt-4 flex items-center gap-3" role="separator">
        <span className="h-px flex-1 bg-white/10" />
        <span className="text-[11px] font-semibold uppercase tracking-[0.12em] text-orange-300/70">
          Continued session starts here
        </span>
        <span className="h-px flex-1 bg-white/10" />
      </div>
    </section>
  );
}
